/**
 * @voltxampere/components — PWL voltage source (Sprint 22 B3)
 *
 * Piecewise-linear voltage stimulus. The waveform is described by
 * a sorted list of (time, voltage) breakpoints; between breakpoints
 * the voltage is linearly interpolated. Outside the breakpoint
 * range the source clamps to the first / last value.
 *
 * The breakpoint list is **JSON-encoded** in the `points` string
 * parameter. This avoids touching `ParamValueSchema` (which today
 * rejects arrays — extending the union would force a schema-version
 * bump and a migration). Sprint 23+ may revisit this once PWL,
 * model libraries, and other future array-bearing components share
 * a single canonical encoding; until then the JSON-string slot
 * keeps Sprint 22's blast radius pinned to the components/engine
 * packages.
 *
 * Format:
 *
 *   { "points": "[{\"t\":0,\"v\":0},{\"t\":1e-6,\"v\":5}]" }
 *
 * Constraints (re-validated at solve time by the engine):
 *   - At least one breakpoint
 *   - Strictly ascending `t` values
 *   - Finite numbers throughout
 *   - `t[0] >= 0` (no pre-zero history)
 *
 * DC OP returns `points[0].v`. Transient evaluates the segment
 * containing the current `ctx.t`. AC small-signal is identically
 * zero — the AC stamp wires only the augmented row's structural
 * coefficients.
 *
 * Pin layout mirrors `voltage-source` / `pulse-source` (`pos` top,
 * `neg` bottom).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
/**
 * Schema for the JSON-decoded PWL breakpoint list. The component's
 * `parameterSchema` validates that `points` is a string with valid
 * inner JSON; the engine stamp re-parses + re-validates the inner
 * array against this schema at solve time.
 */
export const PWLPointSchema = z
    .object({
    t: z.number().nonnegative().finite(),
    v: z.number().finite(),
})
    .strict();
export const PWLPointsSchema = z
    .array(PWLPointSchema)
    .min(1, "PWL source must have at least one breakpoint")
    .superRefine((points, ctx) => {
    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        if (prev !== undefined && curr !== undefined && curr.t <= prev.t) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `breakpoint #${String(i)} t=${String(curr.t)} must be strictly greater than t=${String(prev.t)}`,
            });
        }
    }
});
/**
 * Zod refinement that walks the JSON string and validates the
 * inner array shape. We surface the failure as a Zod issue so
 * authors get the same diagnostic surface as scalar parameter
 * errors.
 */
function refineJSONPoints(raw, ctx) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch (err) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `points must be valid JSON: ${err instanceof Error ? err.message : String(err)}`,
        });
        return;
    }
    const result = PWLPointsSchema.safeParse(parsed);
    if (!result.success) {
        for (const issue of result.error.issues) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: issue.message,
            });
        }
    }
}
export const PWLSourceParamsSchema = z
    .object({
    /**
     * JSON-encoded breakpoint array. See file-level docstring for
     * the format. The engine stamp re-parses this at solve time —
     * authors should not assume a runtime PWLPoints[] is exposed
     * on the component instance.
     */
    points: z.string().superRefine(refineJSONPoints),
})
    .strict();
/**
 * Helper for fixture authors and SPICE codecs — encode an array
 * of breakpoints as the JSON string that `params.points` expects.
 * Pure pass-through with the input array length validated up-front.
 */
export function encodePWLPoints(points) {
    if (points.length === 0) {
        throw new Error("encodePWLPoints: at least one breakpoint required");
    }
    return JSON.stringify(points);
}
export const pwlSourceDefinition = {
    type: "pwl-source",
    category: "source",
    displayName: { tr: "PWL Kaynağı", en: "PWL Source" },
    symbol: "VPWL",
    pins: [
        { id: "pos", side: "top", offset: 0, label: "+" },
        { id: "neg", side: "bottom", offset: 0, label: "-" },
    ],
    parameterSchema: PWLSourceParamsSchema,
    nonlinear: false,
};
//# sourceMappingURL=pwl-source.js.map